import { useState, useEffect } from "react";
import { useLenis } from "lenis/react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Search } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import { SearchOverlay } from "./SearchOverlay";

const Logo = ({ onClick, logo, name }) => (
  <button data-testid="nav-logo" onClick={onClick} className="flex items-center gap-2.5 group -ml-1 shrink-0">
    <img src={logo || "/karams-logo.png"} alt="Karams Studio" data-testid="nav-logo-img"
      className="h-12 xl:h-14 w-auto transition-transform duration-400 group-hover:scale-105" />
    <span data-testid="nav-brand-name" className="font-sans font-semibold text-base md:text-lg tracking-[0.18em] text-white whitespace-nowrap inline lg:hidden">
      {name || "KARAMS STUDIO"}
    </span>
  </button>
);

const hrefFor = (it) => {
  if (it.type === "page") return `/p/${it.target || ""}`;
  if (it.type === "path") return it.target || "/";
  if (it.type === "url") return it.target || "#";
  return null; // section → onClick scroll
};

const linkCls = "relative uppercase text-secondary2 hover:text-white transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-electric hover:after:w-full after:transition-all after:duration-500";

export const Navbar = () => {
  const lenis = useLenis();
  const { content } = useContent();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (window.location.pathname !== "/") { window.location.href = `/#${id}`; return; }
    const el = document.getElementById(id);
    if (el && lenis) lenis.scrollTo(el, { offset: 0, duration: 1.4 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => { const el = document.getElementById(id); if (el) el.scrollIntoView(); }, 700);
    }
  }, []);

  const nav = content.nav || { items: [], cta: {} };
  const items = (nav.items || []).filter((it) => it.visible !== false);
  const cta = nav.cta || {};

  // Réglages admin (nav.settings) : taille/espacement/hauteur pilotables, aperçu en direct.
  // Compact automatique entre 1024 et 1280px pour éviter tout chevauchement.
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 1400);
  useEffect(() => { const h = () => setVw(window.innerWidth); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);
  const st = (() => { const s = { fontSize: 13, gap: 22, height: 80, ...(nav.settings || {}) }; const k = vw < 1280 ? 0.88 : 1; return { fontSize: Math.round(s.fontSize * k * 10) / 10, gap: Math.round(s.gap * k), height: s.height }; })();
  const linkStyle = { fontSize: st.fontSize, letterSpacing: "0.12em" };

  // A single leaf link (desktop). Section → button; others → anchor.
  const Leaf = ({ it, cls, onClickExtra, style }) => {
    const href = hrefFor(it);
    const tid = `nav-link-${it.target || it.id}`;
    if (href === null) {
      return <button data-testid={tid} onClick={() => { go(it.target); onClickExtra?.(); }} className={cls} style={style}>{it.label}</button>;
    }
    const external = it.type === "url";
    return <a data-testid={tid} href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} onClick={() => onClickExtra?.()} className={cls} style={style}>{it.label}</a>;
  };

  const ctaAction = () => {
    if (cta.type === "section" || !cta.type) return go(cta.target || "contact");
    const href = hrefFor(cta);
    if (cta.type === "url") window.open(href, "_blank");
    else window.location.href = href;
  };

  return (
    <>
      <header data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-[9990] transition-[background-color,backdrop-filter,border-color] duration-500 ${scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "border-b border-transparent"}`}>
        <nav className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-5 2xl:px-12 flex items-center gap-3 2xl:gap-4" style={{ height: st.height }}>
          <Logo onClick={() => go("hero")} logo={content.brand.logo} name={content.brand.name} />

          <ul className="hidden lg:flex flex-1 min-w-0 items-center justify-center" style={{ gap: st.gap }}>
            {items.map((it) => {
              if (it.type === "group") {
                const kids = (it.children || []).filter((c) => c.visible !== false);
                return (
                  <li key={it.id} className="relative"
                    onMouseEnter={() => setOpenGroup(it.id)} onMouseLeave={() => setOpenGroup(null)}>
                    <button data-testid={`nav-group-${it.id}`} style={linkStyle} className={`${linkCls} inline-flex items-center gap-1 whitespace-nowrap`}>
                      {it.label} <ChevronDown size={13} className={`transition-transform ${openGroup === it.id ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {openGroup === it.id && kids.length > 0 && (
                        <motion.ul initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.2 }}
                          className="absolute left-0 top-full pt-4 min-w-[220px]">
                          <div className="rounded-lg border border-white/10 bg-black/90 backdrop-blur-xl p-2">
                            {kids.map((c) => (
                              <li key={c.id}>
                                <Leaf it={c} cls="block px-4 py-2.5 rounded text-[12px] tracking-[0.1em] uppercase text-secondary2 hover:text-electric hover:bg-white/5 transition-colors whitespace-nowrap" onClickExtra={() => setOpenGroup(null)} />
                              </li>
                            ))}
                          </div>
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </li>
                );
              }
              return <li key={it.id}><Leaf it={it} cls={`${linkCls} whitespace-nowrap`} style={linkStyle} /></li>;
            })}
          </ul>

          <div className="hidden lg:flex items-center gap-2 xl:gap-3 shrink-0">
            <button data-testid="nav-search-btn" onClick={() => setSearchOpen(true)} aria-label="Rechercher"
              className="inline-flex items-center justify-center h-10 w-10 text-secondary2 hover:text-electric transition-colors">
              <Search size={19} />
            </button>
            {cta.visible !== false && cta.label && (
              <button data-testid="nav-quote-btn" onClick={ctaAction}
                className="inline-flex items-center whitespace-nowrap px-3 xl:px-4 2xl:px-5 py-2.5 text-[10px] xl:text-[12px] tracking-[0.14em] uppercase bg-surface text-white border border-white/10 hover:border-electric hover:text-electric hover:glow-electric transition-[color,border-color,box-shadow] duration-400 rounded-full">
                {cta.label}
              </button>
            )}
          </div>

          <div className="flex lg:hidden items-center gap-4 ml-auto shrink-0">
            <button data-testid="nav-search-btn-mobile" onClick={() => setSearchOpen(true)} aria-label="Rechercher" className="text-white">
              <Search size={24} />
            </button>
            <button data-testid="nav-menu-toggle" className="text-white" onClick={() => setOpen(true)}>
              <Menu size={26} />
            </button>
          </div>
        </nav>
      </header>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[9995] bg-[#050505] flex flex-col overflow-y-auto"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
            <div className="h-20 px-6 flex items-center justify-end shrink-0">
              <button data-testid="nav-menu-close" onClick={() => setOpen(false)} className="text-white"><X size={28} /></button>
            </div>
            <ul className="flex-1 flex flex-col justify-center gap-1 px-8 py-10">
              {items.map((it, i) => (
                <motion.li key={it.id} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.06 }}>
                  {it.type === "group" ? (
                    <div className="py-1">
                      <div className="font-serif text-3xl md:text-4xl text-white/50">{it.label}</div>
                      <div className="pl-4 flex flex-col gap-1 mt-1">
                        {(it.children || []).filter((c) => c.visible !== false).map((c) => (
                          <Leaf key={c.id} it={c} cls="font-serif text-2xl md:text-3xl text-white hover:text-electric transition-colors text-left" onClickExtra={() => setOpen(false)} />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Leaf it={it} cls="font-serif text-4xl md:text-5xl text-white hover:text-electric transition-colors text-left" onClickExtra={() => setOpen(false)} />
                  )}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
